"use client";

import { useAuth, useUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useStatisticsConsent } from "@/legal/cookie-consent/useCookiebotConsent";
import {
  isCaptureAllowed,
  isPostHogConfigured,
} from "@/tracking/posthog/config";

const MESSENGER_TOKEN = "9e995e3efef04ef89ef37bb602dce28b";
const FEATURE_FLAG = "dixa-chat";
const IDENTIFIED_USER_KEY = "dixa-identified-user";

/**
 * The user this tab's messenger was identified as. The stored marker is shared
 * by all tabs, so another tab may already have cleared it on sign-out.
 */
let identifiedUserInTab: string | null = null;

declare global {
  interface Window {
    _dixa_?: {
      invoke: (method: string, payload?: unknown) => void;
      pendingCalls?: [string, unknown][];
    };
  }
}

// The loaded messenger replaces the queueing stub, so look it up on every call.
const invoke = (method: string, payload?: unknown) =>
  window._dixa_?.invoke(method, payload);

/** Dixa's embed snippet: queues commands until the messenger has loaded. */
function loadMessenger() {
  if (window._dixa_) return;
  const pendingCalls: [string, unknown][] = [];
  window._dixa_ = {
    pendingCalls,
    invoke: (method, payload) => pendingCalls.push([method, payload]),
  };
  const script = document.createElement("script");
  script.src = "https://messenger.dixa.io/bootstrap.js";
  script.async = true;
  document.head.appendChild(script);
  invoke("init", {
    messengerToken: MESSENGER_TOKEN,
    disableSentryIntegration: true,
  });
}

function readIdentifiedUser(): string | null {
  try {
    return localStorage.getItem(IDENTIFIED_USER_KEY);
  } catch {
    return null;
  }
}

function writeIdentifiedUser(userId: string | null) {
  try {
    if (userId) localStorage.setItem(IDENTIFIED_USER_KEY, userId);
    else localStorage.removeItem(IDENTIFIED_USER_KEY);
  } catch {
    // Blocked storage: Dixa cannot keep an identity between visits either.
  }
}

/**
 * PostHog can only bucket visitors it may remember (Cookiebot "statistics"
 * consent in production). Everyone else gets a new PostHog id on every page
 * load, and the chat would come and go between pages.
 */
function useChatEnabled() {
  const [canBucket, setCanBucket] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useStatisticsConsent((granted) => setCanBucket(isCaptureAllowed(granted)));

  useEffect(() => {
    if (!canBucket || !isPostHogConfigured) return;
    return posthog.onFeatureFlags(() => {
      // Once shown, the chat stays for the rest of the visit.
      if (posthog.isFeatureEnabled(FEATURE_FLAG)) setEnabled(true);
    });
  }, [canBucket]);

  return enabled;
}

/**
 * Tells the messenger who the signed-in user is. Dixa remembers the identity
 * in the browser, so it is purged once that user is signed out, also when the
 * sign-out happened in another tab or the session expired. The identity names
 * the customer, so it is set again when the user switches organization or
 * school.
 */
function useUserIdentity(enabled: boolean) {
  const { isLoaded, user } = useUser();
  const { orgId, sessionClaims } = useAuth();
  const userId = user?.id ?? null;
  const activeSchool = (sessionClaims as CustomJwtSessionClaims | undefined)
    ?.publicMeta?.activeSchool;

  useEffect(() => {
    if (!enabled || !isLoaded) return;

    const identifiedUser = (identifiedUserInTab ??= readIdentifiedUser());
    if (identifiedUser && identifiedUser !== userId) {
      invoke("purgeUserIdentity");
      identifiedUserInTab = null;
      writeIdentifiedUser(null);
    }
    if (!userId) return;

    let cancelled = false;
    fetch("/api/user-info/dixa-identity")
      .then((response) => (response.status === 200 ? response.json() : null))
      .then((identity) => {
        if (cancelled || !identity) return;
        invoke("setUserIdentity", identity);
        identifiedUserInTab = userId;
        writeIdentifiedUser(userId);
      })
      // Without an identity the user can still chat, anonymously.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, isLoaded, userId, orgId, activeSchool]);
}

/**
 * Dixa chat with customer service, rolled out to a share of visitors with the
 * PostHog flag `dixa-chat`.
 */
export function DixaMessenger() {
  const enabled = useChatEnabled();

  // Declared before useUserIdentity, so `init` is queued before identity calls.
  useEffect(() => {
    if (enabled) loadMessenger();
  }, [enabled]);

  useUserIdentity(enabled);

  return null;
}
